/**
 * Paths and helpers for eposnow.api.booking (`/v1/booking`).
 * OpenAPI: https://gitlab.eposnow.io/hospitality/booking/eposnow.api.booking/-/blob/main/docs/swagger.json
 * Browser calls resolve `/api/...` and `/v1/booking/...` via `resolveBookingApiUrl` using `API_V1_HOSTNAME` in localStorage.
 *
 * Routes used by this app that are not in the booking OpenAPI (often gateway or other services): `GET /api/public/booking/verify-payment`, `POST /api/admin/upload`, `POST /api/account/request-link`, `GET /api/account/bookings`. Calendar/home may call non-admin paths (e.g. legacy list routes).
 */

import type { Service, Staff } from "@shared/schema";
import { API_V1_HOSTNAME_KEY } from "../localStorageKeys";

export const BOOKING_API_PREFIX = "/v1/booking";

/**
 * Whether a URL targets anonymous public booking routes (`/v1/booking/public/...` after resolve, or `/api/public/...` before).
 */
export function isPublicBookingApiPath(resolvedOrRelative: string): boolean {
  try {
    const pathname = resolvedOrRelative.startsWith("http://") || resolvedOrRelative.startsWith("https://")
      ? new URL(resolvedOrRelative).pathname
      : resolvedOrRelative.split("?")[0];
    return pathname.includes("/booking/public/") || pathname.startsWith("/api/public/");
  } catch {
    return resolvedOrRelative.includes("/booking/public/") || resolvedOrRelative.includes("/api/public/");
  }
}

/**
 * `credentials` for browser `fetch` to public booking APIs.
 * Cross-origin `include` needs the API to return `Access-Control-Allow-Origin: <your exact origin>` and
 * `Access-Control-Allow-Credentials: true`. Curl ignores CORS, so it can succeed while the browser blocks.
 * Default `omit` avoids credentialed CORS (typical for anonymous public GETs). Set
 * `REACT_APP_BOOKING_PUBLIC_FETCH_CREDENTIALS=include` if the gateway requires cookies (e.g. LB stickiness).
 */
export function publicFetchCredentials(): RequestCredentials {
  const v = process.env.REACT_APP_BOOKING_PUBLIC_FETCH_CREDENTIALS;
  if (v === "include") return "include";
  return "omit";
}

/**
 * Maps relative booking routes to the real API: `{API_V1_HOSTNAME}booking/...` (v1 base includes trailing slash).
 */
export function resolveBookingApiUrl(path: string): string {
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }
  const base = localStorage.getItem(API_V1_HOSTNAME_KEY) ?? "";
  if (path.startsWith("/api")) {
    const rest = path.replace(/^\/api\/?/, "");
    return `${base}booking/${rest}`;
  }
  if (path.startsWith("/v1/booking")) {
    const rest = path.replace(/^\/v1\/booking\/?/, "");
    return `${base}booking/${rest}`;
  }
  return path;
}

/** Mirrors `eposnow_api_booking_internal_common.ListMetadata` in OpenAPI. */
export type ListMetadata = {
  page: number | null;
  limit: number | null;
  totalPages: number | null;
  totalRecords: number | null;
  currentPageSize: number | null;
};

/** Mirrors `eposnow_api_booking_internal_common.ListLinks` in OpenAPI (`_links` on the wire). */
export type ListLinks = {
  baseUrl: string | null;
  firstPage: string | null;
  lastPage: string | null;
  nextPage: string | null;
  previousPage: string | null;
};

function parseOptionalNumber(v: unknown): number | null {
  if (v == null || v === "") return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

function parseOptionalString(v: unknown): string | null {
  if (v == null) return null;
  const s = String(v).trim();
  return s === "" ? null : s;
}

/**
 * Parses `PagedListResponse`-shaped JSON (`data`, optional `metadata`, `_links`) or a raw array.
 * Safe on partial / null fields from the API.
 */
export function parsePagedList<T = unknown>(data: unknown): {
  items: T[];
  metadata: ListMetadata | null;
  links: ListLinks | null;
} {
  if (Array.isArray(data)) {
    return { items: data as T[], metadata: null, links: null };
  }
  if (!data || typeof data !== "object") {
    return { items: [], metadata: null, links: null };
  }
  const o = data as Record<string, unknown>;
  const raw = o.data;
  const items: T[] = Array.isArray(raw) ? (raw as T[]) : [];

  let metadata: ListMetadata | null = null;
  if (o.metadata && typeof o.metadata === "object" && o.metadata !== null) {
    const m = o.metadata as Record<string, unknown>;
    metadata = {
      page: parseOptionalNumber(m.page),
      limit: parseOptionalNumber(m.limit),
      totalPages: parseOptionalNumber(m.totalPages),
      totalRecords: parseOptionalNumber(m.totalRecords),
      currentPageSize: parseOptionalNumber(m.currentPageSize),
    };
  }

  let links: ListLinks | null = null;
  // API returns HAL-style `_links`; `no-underscore-dangle` disallows that identifier in dot form.
  // eslint-disable-next-line no-underscore-dangle -- wire contract
  const linkRaw = o._links;
  if (linkRaw && typeof linkRaw === "object" && linkRaw !== null) {
    const m = linkRaw as Record<string, unknown>;
    links = {
      baseUrl: parseOptionalString(m.baseUrl),
      firstPage: parseOptionalString(m.firstPage),
      lastPage: parseOptionalString(m.lastPage),
      nextPage: parseOptionalString(m.nextPage),
      previousPage: parseOptionalString(m.previousPage),
    };
  }

  return { items, metadata, links };
}

/** Rows only — use `parsePagedList` when you need `metadata` / `_links`. */
export function unwrapPagedList<T = unknown>(data: unknown): T[] {
  return parsePagedList<T>(data).items;
}

export function tryGetPublicSlug(): string | null {
  const slug = process.env.REACT_APP_BOOKING_PUBLIC_SLUG as string | undefined;
  return slug?.trim() || null;
}

/**
 * Slug for `/v1/booking/public/{slug}/...` (e.g. public booking page, admin drawer when combined with `/api/admin/business` slug).
 * Prefer `routeSlug` when the public booking page is opened at `/book/:slug` (see `App.tsx`).
 * Otherwise uses `REACT_APP_BOOKING_PUBLIC_SLUG` if set. Admin UI should use the business slug from settings when available.
 */
export function resolvePublicSlug(routeSlug?: string | null): string | null {
  const fromRoute = routeSlug?.trim();
  if (fromRoute) return fromRoute;
  const slug = tryGetPublicSlug();
  if (slug) return slug;
  return null;
}

/**
 * Same-origin-style `/api/public/...` paths; use with `resolveBookingApiUrl` when calling `fetch`.
 * Public routes require the business slug in the path (see swagger: `/v1/booking/public/{slug}/...`).
 */
export function apiPublicPath(slug: string, pathAndQuery: string): string {
  const q = pathAndQuery.startsWith("/") ? pathAndQuery : `/${pathAndQuery}`;
  return `/api/public/${encodeURIComponent(slug)}${q}`;
}

export function adminPath(path: string): string {
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${BOOKING_API_PREFIX}/admin${p}`;
}

/**
 * Browser-relative admin paths resolved by `resolveBookingApiUrl` to `/v1/booking/admin/...`
 * (same contract as `adminPath` after mapping).
 */
export const API_ADMIN_PREFIX = "/api/admin";

export function apiAdminPath(path: string): string {
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${API_ADMIN_PREFIX}${p}`;
}

export function publicPath(path: string): string {
  const slug = tryGetPublicSlug();
  const p = path.startsWith("/") ? path : `/${path}`;
  if (!slug) {
    return `${BOOKING_API_PREFIX}/public/_/invalid${p}`;
  }
  return `${BOOKING_API_PREFIX}/public/${encodeURIComponent(slug)}${p}`;
}

export type BookingApiBusiness = {
  companyId: string;
  slug: string;
  name: string;
  timezone: string;
  currency: string;
  defaultDepositEnabled: boolean;
  defaultDepositType: string;
  defaultDepositValue: number;
  slotIncrementMins: number;
  minLeadTimeMins: number;
  maxBookingHorizonDays: number;
  cancellationPolicyText?: string | null;
  /** Public profile copy from business settings (shown on the booking page). */
  description?: string | null;
  address?: string | null;
  phone?: string | null;
  email?: string | null;
  brandingJson?: string | null;
  bookingPageConfig?: string | null;
  bookingPageDraftConfig?: string | null;
};

export type BookingApiService = {
  id: number;
  businessId: string;
  name: string;
  desc?: string | null;
  durationMins: number;
  bufferBeforeMins: number;
  bufferAfterMins: number;
  priceCents: number;
  currency: string;
  depositEnabled?: boolean | null;
  depositType?: string | null;
  depositValue?: number | null;
  active: boolean;
  staffIds?: number[];
};

export type BookingApiStaff = {
  id: number;
  businessId: string;
  displayName: string;
  email?: string | null;
  active: boolean;
};

export type BookingApiSlot = {
  startAt: string;
  endAt: string;
  staffId: number;
};

export type BookingApiBooking = {
  id: string;
  businessId: string;
  serviceId: number;
  staffId?: number | null;
  customerEmail: string;
  customerName?: string | null;
  customerPhone?: string | null;
  startAt: string;
  endAt: string;
  status: string;
  source: string;
  priceCents: number;
  depositAmountCents: number;
  currency: string;
};

/** Map public/admin API service payloads to UI `Service` rows (string ids). */
export function mapServiceResponse(s: BookingApiService): Service {
  return {
    id: String(s.id),
    businessId: s.businessId,
    name: s.name,
    description: s.desc ?? null,
    category: "General",
    durationMins: s.durationMins,
    bufferBeforeMins: s.bufferBeforeMins ?? 0,
    bufferAfterMins: s.bufferAfterMins ?? 0,
    priceCents: s.priceCents,
    currency: s.currency,
    depositEnabled: s.depositEnabled ?? false,
    depositType: s.depositType ?? "fixed",
    depositValue: s.depositValue ?? 0,
    depositPolicyText: null,
    active: s.active,
  } as Service;
}

/** Map public/admin API staff payloads to UI `Staff` rows (string ids). */
export function mapStaffResponse(s: BookingApiStaff): Staff {
  return {
    id: String(s.id),
    businessId: s.businessId,
    displayName: s.displayName,
    email: s.email ?? null,
    phone: null,
    avatarUrl: null,
    role: "staff",
    bio: null,
    active: s.active,
  } as Staff;
}

/**
 * JSON body for create/update booking: API accepts integer ids; mocks may use opaque string ids (`svc-1`).
 * `Number("svc-1")` is NaN — use this instead of raw `Number(id)`.
 */
export function coerceBookingApiEntityId(id: string): number | string {
  const t = id.trim();
  if (t === "") return id;
  if (/^-?\d+$/.test(t)) {
    const n = Number(t);
    if (Number.isSafeInteger(n)) return n;
  }
  return id;
}

export function parseJsonBytesField<T>(raw: unknown): T | null {
  if (raw == null) return null;
  if (typeof raw === "object" && !Array.isArray(raw)) return raw as T;
  if (typeof raw !== "string") return null;
  try {
    const binary = atob(raw);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    const text = new TextDecoder().decode(bytes);
    return JSON.parse(text) as T;
  } catch {
    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  }
}

export function resolveDepositCents(
  business: {
    defaultDepositEnabled: boolean;
    defaultDepositType: string;
    defaultDepositValue: number;
  },
  service: {
    priceCents: number;
    depositEnabled?: boolean | null;
    depositType?: string | null;
    depositValue?: number | null;
  },
): number {
  let enabled = business.defaultDepositEnabled;
  let depType = business.defaultDepositType;
  let value = business.defaultDepositValue;
  if (service.depositEnabled != null) enabled = service.depositEnabled;
  if (service.depositType != null) depType = service.depositType;
  if (service.depositValue != null) value = service.depositValue;
  if (!enabled) return 0;
  const t = (depType || "").toUpperCase();
  if (t === "PERCENT") return Math.floor((service.priceCents * value) / 100);
  return value;
}

export type UiSlot = {
  time: string;
  display: string;
  startAt: string;
  staffId: number;
};

export function slotsToUi(slots: BookingApiSlot[]): UiSlot[] {
  const sorted = [...slots].sort((a, b) => {
    const ta = new Date(a.startAt).getTime();
    const tb = new Date(b.startAt).getTime();
    if (ta !== tb) return ta - tb;
    return a.staffId - b.staffId;
  });
  const byTime = new Map<string, UiSlot>();
  for (const s of sorted) {
    const d = new Date(s.startAt);
    const time = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    if (byTime.has(time)) continue;
    const h = d.getHours();
    const period = h >= 12 ? "PM" : "AM";
    const displayHour = h % 12 || 12;
    const m = d.getMinutes();
    const display = `${displayHour}:${String(m).padStart(2, "0")} ${period}`;
    byTime.set(time, { time, display, startAt: s.startAt, staffId: s.staffId });
  }
  return Array.from(byTime.values()).sort((a, b) => a.time.localeCompare(b.time));
}

export function localDateTimeToIso(dateStr: string, timeHm: string): string {
  const [yy, mm, dd] = dateStr.split("-").map(Number);
  const [h, m] = timeHm.split(":").map(Number);
  const d = new Date(yy, (mm || 1) - 1, dd || 1, h || 0, m || 0, 0, 0);
  return d.toISOString();
}

export function normalizeStatus(status: string): string {
  return status.toLowerCase();
}
